/**
 * OS Window — draggable, resizable (sağ alt köşe), closeable, minimizable, maximizable
 */
import { Maximize2, Minus, X } from 'lucide-react'
import { useCallback, useEffect, useRef } from 'react'
import { useWindowManager } from './WindowManagerContext'
import type { OSWindowState } from './types'

interface Props {
  win: OSWindowState
}

export default function OSWindow({ win }: Props) {
  const { closeWindow, focusWindow, minimizeWindow, maximizeWindow, updatePosition, updateSize } = useWindowManager()
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null)
  const resizeRef = useRef<{ startX: number; startY: number; origW: number; origH: number } | null>(null)
  const Component = win.component

  // Drag via title bar
  const onTitleMouseDown = useCallback((e: React.MouseEvent) => {
    if (win.maximized) { return }
    e.preventDefault()
    focusWindow(win.id)
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: win.position.x, origY: win.position.y }

    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) { return }
      updatePosition(win.id, {
        x: Math.max(0, dragRef.current.origX + ev.clientX - dragRef.current.startX),
        y: Math.max(0, dragRef.current.origY + ev.clientY - dragRef.current.startY),
      })
    }
    const onUp = () => { dragRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [win, focusWindow, updatePosition])

  // Resize via bottom-right handle
  const onResizeMouseDown = useCallback((e: React.MouseEvent) => {
    if (win.maximized) { return }
    e.preventDefault()
    e.stopPropagation()
    focusWindow(win.id)
    resizeRef.current = { startX: e.clientX, startY: e.clientY, origW: win.size.width, origH: win.size.height }

    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) { return }
      updateSize(win.id, {
        width: Math.max(320, resizeRef.current.origW + ev.clientX - resizeRef.current.startX),
        height: Math.max(240, resizeRef.current.origH + ev.clientY - resizeRef.current.startY),
      })
    }
    const onUp = () => { resizeRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [win, focusWindow, updateSize])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') { closeWindow(win.id) } }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [win.id, closeWindow])

  if (win.minimized) { return null }

  const style: React.CSSProperties = win.maximized
    ? { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', borderRadius: 0 }
    : { position: 'absolute', top: win.position.y, left: win.position.x, width: win.size.width, height: win.size.height }

  return (
    <div
      onClick={() => focusWindow(win.id)}
      style={{
        ...style,
        background: 'var(--os-win-bg)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: '1px solid var(--os-border)',
        borderRadius: win.maximized ? 0 : '12px',
        boxShadow: 'var(--os-win-shadow)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: win.zIndex,
        animation: 'winIn .25s ease-out',
      }}
    >
      <style>{`@keyframes winIn{from{transform:scale(.97);opacity:0}to{transform:scale(1);opacity:1}}`}</style>

      {/* Title Bar */}
      <div
        onMouseDown={onTitleMouseDown}
        style={{
          display: 'flex', alignItems: 'center', height: '34px', padding: '0 12px',
          background: 'var(--os-win-tb)',
          borderBottom: '1px solid var(--os-border)',
          cursor: win.maximized ? 'default' : 'grab',
          flexShrink: 0, userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', gap: '6px', marginRight: '10px' }}>
          <button onClick={e => { e.stopPropagation(); closeWindow(win.id) }}
            style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#ff5f57', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={6} color="rgba(0,0,0,0.4)" />
          </button>
          <button onClick={e => { e.stopPropagation(); minimizeWindow(win.id) }}
            style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#febc2e', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Minus size={6} color="rgba(0,0,0,0.4)" />
          </button>
          <button onClick={e => { e.stopPropagation(); maximizeWindow(win.id) }}
            style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#28c840', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Maximize2 size={6} color="rgba(0,0,0,0.4)" />
          </button>
        </div>
        <div style={{ flex: 1, textAlign: 'center', fontSize: '11px', fontWeight: 600, color: 'var(--os-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', pointerEvents: 'none' }}>
          {win.title}
        </div>
      </div>

      {/* Content */}
      <div style={{
        flex: 1, overflow: 'auto', position: 'relative',
        background: 'var(--os-win-bg)',
        color: 'var(--os-text)',
      }}>
        {win.noPadding
          ? <Component {...(win.props ?? {})} />
          : (
            <div style={{ padding: '16px', minHeight: '100%', boxSizing: 'border-box' }}>
              <Component {...(win.props ?? {})} />
            </div>
          )
        }
      </div>

      {/* Resize handle — bottom right */}
      {!win.maximized && (
        <div
          onMouseDown={onResizeMouseDown}
          style={{
            position: 'absolute', bottom: 0, right: 0,
            width: '16px', height: '16px',
            cursor: 'se-resize',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
            padding: '3px',
          }}
        >
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
            <path d="M7 1L1 7M7 4L4 7M7 7L7 7" stroke="var(--os-dim)" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </div>
      )}
    </div>
  )
}
